import React, { useEffect } from 'react';
import { useForm, useFieldArray, SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Loader2, PlusCircle, Trash2 } from 'lucide-react';
import { TaxRate } from '@/app/types/taxRate';
import { TaxComponent, TaxRateComponentLink } from '@/app/types/taxComponent'; // Use updated types


// --- Zod Schema for FORM state (uses strings for numbers initially) ---
const componentLinkSchema = z.object({
  tax_component_id: z.number({ required_error: "Component selection is required" }),
  // Rate as string for input, validated as number
  rate: z.string().refine(val => !isNaN(parseFloat(val)) && parseFloat(val) >= 0, {
    message: "Rate must be a non-negative number",
  }),
});

const formSchema = z.object({
  name: z.string().min(1, 'Tax rate name is required').max(50),
  code: z.string().min(1, 'Code is required').max(20)
      .refine(s => !/\s/.test(s), 'Code cannot contain spaces'), // Added code field
  // Rate as string for input, validated as number - will be auto-calculated
  rate: z.string(),
  is_active: z.boolean(),
  components: z.array(componentLinkSchema).min(1, "At least one tax component is required"), // Require at least one component
});

type TaxRateFormValues = z.infer<typeof formSchema>;

// --- Interface matching the BACKEND payload structure ---
interface TaxRateServicePayload {
  name: string;
  code: string; // Added code
  rate: number; // Overall rate as number
  is_active: boolean;
  components: TaxRateComponentLink[]; // Array of component links with rates as numbers
}


interface TaxRateFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: TaxRateServicePayload) => void; // Expects backend payload structure
  defaultValues?: Partial<TaxRate>; // Default values from fetched data
  availableComponents: TaxComponent[]; // Pass available components for selection
  isLoading?: boolean;
  mode: 'create' | 'edit';
}

const TaxRateForm: React.FC<TaxRateFormProps> = ({
  open,
  onOpenChange,
  onSubmit,
  defaultValues,
  availableComponents,
  isLoading = false,
  mode,
}) => {

  // --- Form Initialization ---
  const form = useForm<TaxRateFormValues>({
    resolver: zodResolver(formSchema),
    // Map defaultValues (backend structure) to form structure (strings, component links)
    defaultValues: {
      name: defaultValues?.name || '',
      code: defaultValues?.code || '', // Added code default
      rate: String(defaultValues?.rate ?? 0), // Total rate as string
      is_active: defaultValues?.is_active ?? true,
      components: defaultValues?.components?.map(compDetail => ({
        tax_component_id: compDetail.tax_component.id, // Use ID from nested component
        rate: String(compDetail.rate ?? 0), // Component rate as string
      })) || [],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "components",
  });

  // --- Simple auto-update total rate based on components ---
  const componentRates = form.watch("components");
  useEffect(() => {
    if (componentRates && componentRates.length > 0) {
      // Simple sum calculation using standard JavaScript
      const sum = componentRates.reduce((acc, comp) => {
        const rate = parseFloat(comp.rate || "0");
        return !isNaN(rate) ? acc + rate : acc;
      }, 0);
      
      // Update the total rate field with the formatted sum
      form.setValue("rate", sum.toFixed(2));
    } else {
      form.setValue("rate", "0.00");
    }
  }, [componentRates, form]);

  // --- Submission Handler ---
  const handleSubmit: SubmitHandler<TaxRateFormValues> = (values) => {
    // Calculate the total rate from components to ensure consistency
    const componentsTotal = values.components.reduce((sum, comp) => {
      const rate = parseFloat(comp.rate || "0");
      return !isNaN(rate) ? sum + rate : sum;
    }, 0);
    
    // Explicitly convert component rates to ensure they are valid numbers
    const formattedComponents = values.components.map(comp => ({
      tax_component_id: comp.tax_component_id,
      rate: parseFloat(comp.rate || "0") // Convert to number
    }));
    
    // Log for debugging
    console.log("Components:", formattedComponents);
    console.log("Components Total:", componentsTotal);
    
    // Convert form values to the backend payload structure
    // Making sure the rate matches the sum of component rates exactly
    const payload: TaxRateServicePayload = {
      name: values.name,
      code: values.code, 
      rate: componentsTotal, // Use the calculated total to match components
      is_active: values.is_active,
      components: formattedComponents,
    };
    
    // Final payload check
    console.log("Final payload:", JSON.stringify(payload, null, 2));
    
    // Submit the consistent payload
    onSubmit(payload);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh]">
        <DialogHeader>
          <DialogTitle>{mode === 'create' ? 'Add New Tax Rate' : 'Edit Tax Rate'}</DialogTitle>
          <DialogDescription>
            Define the tax rate and its component breakdown.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
             <ScrollArea className="max-h-[65vh] px-6 pt-2 pb-4"> {/* Add padding inside ScrollArea */}
              <div className="space-y-4">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Name*</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="e.g., GST 18%, VAT 5%" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                 <FormField
                  control={form.control}
                  name="code"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Code*</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="e.g., GST18" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="rate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Total Rate (%)*</FormLabel>
                      <FormControl>
                        <Input
                          type="text"
                          {...field}
                          placeholder="Calculated from components"
                          readOnly // Make total rate read-only
                          className="font-medium bg-muted/50"
                        />
                      </FormControl>
                       <FormDescription>This is automatically calculated from the component rates below.</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Tax Rate Components Array */}
                <div>
                  <FormLabel>Tax Components*</FormLabel>
                  <FormDescription>
                    Define the components making up this tax rate.
                  </FormDescription>
                  <div className="space-y-2 mt-2">
                    {fields.map((item, index) => (
                      <div key={item.id} className="flex items-start gap-2 p-3 border rounded-md bg-background">
                        {/* Component Selection Dropdown */}
                        <FormField
                          control={form.control}
                          name={`components.${index}.tax_component_id`}
                          render={({ field }) => (
                            <FormItem className="flex-1">
                              <FormLabel className="sr-only">Component</FormLabel>
                              <Select
                                value={field.value?.toString() || ''}
                                onValueChange={(value) => field.onChange(value ? parseInt(value) : 0)}
                              >
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select Component" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {availableComponents.map((comp) => (
                                    <SelectItem key={comp.id} value={comp.id.toString()}>
                                      {comp.name} ({comp.code})
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        {/* Component Rate Input */}
                        <FormField
                          control={form.control}
                          name={`components.${index}.rate`}
                          render={({ field }) => (
                            <FormItem className="w-24"> {/* Fixed width */}
                              <FormLabel className="sr-only">Rate %</FormLabel>
                              <FormControl>
                                <Input
                                  type="number"
                                  step="0.01"
                                  {...field}
                                  placeholder="Rate %"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        {/* Remove Button */}
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => remove(index)}
                          className="text-destructive hover:text-destructive mt-1 shrink-0" // Added shrink-0
                        >
                          <Trash2 className="h-4 w-4" />
                          <span className="sr-only">Remove Component</span>
                        </Button>
                      </div>
                    ))}
                     {/* Display errors specifically for the components array minimum length */}
                    {form.formState.errors.components && form.formState.errors.components.type === 'too_small' && (
                        <p className="text-sm font-medium text-destructive mt-1">
                            {form.formState.errors.components.message}
                        </p>
                    )}
                  </div>

                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => append({ tax_component_id: 0, rate: '0' })} // Use 0 as default value
                    className="mt-2"
                  >
                    <PlusCircle className="mr-2 h-4 w-4" /> Add Component
                  </Button>
                </div>

                <FormField
                  control={form.control}
                  name="is_active"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center space-x-3 space-y-0 pt-2">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                      <div className="space-y-1 leading-none">
                        <FormLabel className="font-normal">Active</FormLabel>
                        <FormDescription>
                          Inactive tax rates won&apos;t be available.
                        </FormDescription>
                      </div>
                    </FormItem>
                  )}
                />
              </div>
            </ScrollArea>

            <DialogFooter className="px-6 pb-4 pt-2"> {/* Added padding */}
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                {isLoading
                  ? (mode === 'create' ? 'Creating...' : 'Updating...')
                  : (mode === 'create' ? 'Create Tax Rate' : 'Save Changes')
                }
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default TaxRateForm;